<template>
  <div class="tags-container"
       @focus="onOuterFocus">
    <IhuTag v-for="tag in groupedTags"
            :key="tag[itemIdAttr]"
            :removable="isRemovable(tag)"
            @remove="() => remove(tag)">
      {{ tag[itemValueAttr] }}
    </IhuTag>
    <div class="arrow" :class="{ expanded: opened }"></div>
    <div class="search-container"
         :style="inputContainerStyle">
      <div
        ref="searchInput"
        :class="['search-input', { 'placeholder-icon': placeholderIconClass, 'placeholder-displayed': placeholderOnNoSelection }, placeholderIconClass]"
        :contenteditable="enabled"
        @focus.prevent="emitFocusEvent"
        @blur.prevent="inputBlurEvent"
        @keydown.enter.prevent
        @keyup="onKeyup"
        v-text="placeholderOnNoSelection">
        {{ searchInputText }}
      </div>
    </div>
  </div>
</template>

<script>
  import { debounce, get } from 'lodash';
  import IhuTag from '@/components/common/ihu-tag';

  export default {
    name: 'ihu-select-tags',
    components: {
      IhuTag,
    },
    props: {
      tags: {
        type: Array,
        default: () => ([]),
      },
      maxTagsCount: {
        type: Number,
        default: 5,
      },
      searchTerm: {
        type: String,
        default: '',
      },
      itemIdAttr: {
        type: String,
        default: 'id',
      },
      itemValueAttr: {
        type: String,
        default: 'value',
      },
      placeholder: {
        type: String,
        default: 'Please Select',
      },
      placeholderIconClass: {
        type: String,
        default: '',
      },
      opened: {
        type: Boolean,
        default: false,
      },
      enabled: {
        type: Boolean,
        default: true,
      },
    },
    data() {
      return {
        searchInputText: this.searchTerm || '',
        searchInputActive: false,
      };
    },
    computed: {
      placeholderOnNoSelection() {
        if (this.tags.length === 0 && !this.searchInputActive) {
          return this.placeholder;
        }
        return '';
      },
      inputContainerStyle() {
        return this.placeholderOnNoSelection || this.searchTerm || this.searchInputHasValue ? { width: 'auto' } : { width: 0 };
      },
      groupedTags() {
        const result = (this.tags || []).reduce(
          (res, item, idx) => {
            if (idx < this.maxTagsCount) {
              res.splitted = [...res.splitted, { ...item, removable: true }];
            } else {
              res.rest += 1;
            }
            return res;
          },
          { rest: 0, splitted: [] },
        );
        const rest = result.rest > 0 ? [{ [this.itemIdAttr]: 'other', [this.itemValueAttr]: `+${result.rest}` }] : [];
        return [].concat(result.splitted, rest);
      },
    //   selectedItemValue() {
    //     return this.selectedItem ? this.selectedItem[this.itemValueAttr] : '';
    //   },
    //   shouldShowPlaceholder() {
    //     return !this.selectedItemValue && this.placeholder;
    //   },
    },
    watch: {
      searchTerm() {
        if (this.searchTerm === '') {
          this.$refs.searchInput.innerText = '';
        }
      },
    },
    methods: {
      onTagInputValueChange() {
        if (this.enabled) {
          const text = get(this.$refs, 'searchInput.innerText', '');
          // const textWithFixedSpaces = text.replace(specialSpace, regularSpace);
          // this.$emit('input', textWithFixedSpaces);
          this.$emit('input', text);
        }
      },
      onOuterFocus() {
        if (this.enabled) {
          this.$refs.searchInput.focus();
        }
      },
      inputBlurEvent() {
        this.searchInputActive = false;
      },
      emitFocusEvent() {
        if (this.enabled) {
          this.searchInputActive = true;
          this.$emit('emitFocusEvent', true);
        }
      },
      isRemovable(item) {
        return item && item.removable;
      },
      remove(item) {
        this.$emit('removeItem', item);
      },
      onKeyup: debounce(function searchInput() {
        this.onTagInputValueChange();
      }, 150),
    },
  };
</script>

<style scoped lang="scss">
  .tags-container {
    border-radius: 4px;
    border-style: solid;
    border-width: 1px;
    @include themify {
      background: getColor('ihu-select-bg', 'ihu');
      border-color: getColor('ihu-select-border', 'ihu');
    }

    min-width: 198px;
    min-height: 38px;
    overflow-x: hidden;

    font-size: 14px;
    line-height: 150%;
    outline: none;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    padding: 0 20px 0 12px;
    align-items: center;

    .arrow {
      position: absolute;
      right: 10px;
      top: 40%;
      width: 0;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top-width: 5px;
      border-top-style: solid;
      @include themify {
        border-top-color: getColor('ihu-select-arrow-color', 'ihu');
      }
      transform: rotateZ(0deg) translateY(0px);
      transition-duration: 0.3s;
      transition-timing-function: cubic-bezier(.59,1.39,.37,1.01);

      &.expanded {
        transform: rotateZ(180deg) translateY(2px);
      }
    }

    .search-container {
      display: inline-block;

      // eslint-disable-next-line vue-scoped-css/no-unused-selector
      .search-input {
        font-weight: 500;
        font-size: 12px;
        line-height: 120%;

        border-radius: 1px;
        outline: none;
        word-break: break-all;
        margin-left: 2px;
        padding: 0 5px;

        &.placeholder-displayed {
          @include themify {
            color: getColor('ihu-medallia-input-placeholder', 'ihu');
          }

          &.placeholder-icon:not(:focus) {
            padding-left: 20px;

            &.spyglass:before {
              content: ' ';
              position: absolute;
              left: 15px;
              background-repeat: no-repeat;
              background-size: contain;
              width: 14px;
              height: 14px;
              margin-right: 5px;
            }

            @include themify('dark') {
              &.spyglass:before {
                background-image: url('../assets/ic_search_dt.svg');
              }
            }
            @include themify('light') {
              &.spyglass:before {
                background-image: url('../assets/ic_search_lt.svg');
              }
            }
          }
        }

        &:empty {
          border: none;
          height: 20px;
          line-height: 20px;
        }
      }
    }
  }
</style>
